import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-05c8881f"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "profile-toolbar" }
const _hoisted_2 = { class: "profile-title" }
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("svg", {
  width: "16",
  height: "16",
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  "stroke-width": "2"
}, [
  /*#__PURE__*/_createElementVNode("path", { d: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" }),
  /*#__PURE__*/_createElementVNode("polyline", { points: "7,10 12,15 17,10" }),
  /*#__PURE__*/_createElementVNode("line", {
    x1: "12",
    y1: "15",
    x2: "12",
    y2: "3"
  })
], -1))
const _hoisted_4 = [
  _hoisted_3
]
const _hoisted_5 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("svg", {
  width: "16",
  height: "16",
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  "stroke-width": "2"
}, [
  /*#__PURE__*/_createElementVNode("line", {
    x1: "18",
    y1: "6",
    x2: "6",
    y2: "18"
  }),
  /*#__PURE__*/_createElementVNode("line", {
    x1: "6",
    y1: "6",
    x2: "18",
    y2: "18"
  })
], -1))
const _hoisted_6 = [
  _hoisted_5
]
const _hoisted_7 = { class: "chart-container" }

import { onMounted, onUnmounted, ref, watchEffect, computed, watch } from "vue";
import { useAppStateStore } from "@/store/app-state";
import Chart from "chart.js/auto";
import { ViewIndex } from "@/classes/data-structures/view-index";
import { LayerItemType } from "@/classes/data-structures/layer-item-type";

// --- Types ---
type ChartPoint = { distance: number; elevation: number };
type Profile = { uuid: string; name: string; chartPoints: ChartPoint[]; getColorHex: () => string; unit?: string; userData?: any };
type ProfileGroup = { name: string; profiles: Profile[]; selectedProfiles?: Profile[] };

type ChartDistanceCache = {
  distances: number[];
  ascending: boolean;
  min: number;
  max: number;
};

type WorldDistanceCache = {
  points: any[];
  generation: number;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'ProfileViewer',
  props: {
    viewer: null
  },
  setup(__props: any, { expose }) {

const props = __props as { viewer: any };

const chartDistanceCache = new WeakMap<Profile, ChartDistanceCache>();
const worldDistanceCache = new WeakMap<Profile, WorldDistanceCache>();
let worldDistanceCacheGeneration = 0;


const appState = useAppStateStore();
const viewer = props.viewer.getViewer();

// --- Refs ---
const profileCanvas = ref<HTMLCanvasElement | null>(null);
const profileViewerEl = ref<HTMLDivElement | null>(null);
const tooltipEl = ref<HTMLDivElement | null>(null);
let chartInstance: Chart | null = null;

const verticalExaggeration = ref(1.0);
const selectedProfileGroup = ref<ProfileGroup | null>(null);
// Track hovered x-distance for tooltip title
const hoveredChartDistance = ref<number | null>(null);

// --- Visibility helpers ---
function buildVisibleIdSet(): Set<string> {
  const layers = appState.getViewLayers(ViewIndex.View1, LayerItemType.Profiles) as any[];
  const set = new Set<string>();
  for (const L of layers) {
    if (L.children) {
      if (!L.visible) continue;
      for (const c of L.children) if (c.visible && c.id) set.add(c.id);
    } else if (L.visible && L.id) {
      set.add(L.id);
    }
  }
  return set;
}

function findProfileGroup(selected: any): ProfileGroup | null {
  if (!selected || !viewer?.profileLayers) return null;
  const groupKey = selected.userData?.groupKey;
  if (!groupKey) return null;

  const profiles: Profile[] = (viewer.profiles as Profile[]).filter(p => p.userData?.groupKey === groupKey);
  if (!profiles.length) return null;

  let name = selected.name as string;
  for (const layer of viewer.profileLayers) {
    if (layer.children && layer.children.find((c: any) => c.id === selected.uuid || c.name === selected.name)) { name = layer.name; break; }
  }
  profiles.sort((a, b) => a.name.localeCompare(b.name));
  return { name, profiles };
}

function isTransverseGroup(group: ProfileGroup | null): boolean {
  if (!group || !group.profiles?.length) return false;

  let hasCrossMetadata = false;
  const cloudIds = new Set<string>();

  for (const profile of group.profiles) {
    const meta: any = (profile as any)?.userData ?? {};
    if (meta?.isCrossSection === true || meta?.profileType === "cross" || meta?.distance != null || meta?.parentId != null) {
      hasCrossMetadata = true;
      break;
    }
    const cloudId = meta?.cloudId;
    if (cloudId != null && cloudId !== "") {
      cloudIds.add(String(cloudId));
    }
  }

  if (hasCrossMetadata) return true;
  if (cloudIds.size > 1) return true;
  return false;
}

function getProfileUnit(group: ProfileGroup | null = selectedProfileGroup.value): string {
  return group?.profiles?.find((profile) => profile.unit)?.unit || viewer?.getUnit?.() || "m";
}

// --- Chart data assembly (computed) ---
const chartInput = computed(() => {
  const group = selectedProfileGroup.value;
  if (!group || !group.profiles.length) {
    return { datasets: [], baseMin: 0, baseMax: 0, xMin: 0, xMax: 1 } as any;
  }

  const visibleIds = buildVisibleIdSet();
  const selectedProfiles = group.selectedProfiles || group.profiles;

  // Global elevation range from 2D chart points
  let baseMin = Number.POSITIVE_INFINITY, baseMax = Number.NEGATIVE_INFINITY;
  for (const p of group.profiles) {
    const elevations = p.chartPoints.map(pt => pt.elevation);
    if (elevations.length) { 
      baseMin = Math.min(baseMin, Math.min(...elevations)); 
      baseMax = Math.max(baseMax, Math.max(...elevations)); 
    }
  }
  if (!isFinite(baseMin) || !isFinite(baseMax)) baseMin = baseMax = 0;

  // Build datasets - much simpler now!
  const datasets: any[] = [];
  selectedProfiles.forEach((p) => {
    if (!p.chartPoints?.length) return;

    // Skip non-visible datasets
    const isVisible = visibleIds.has(p.uuid);
    if (!isVisible) return;

    // Direct use of 2D chart points
    const data = p.chartPoints.map(pt => ({
      x: pt.distance,
      y: pt.elevation * verticalExaggeration.value,
      originalY: pt.elevation,
    }));

    const color = `#${p.getColorHex()}`;
    datasets.push({
      label: p.name,
      data,
      borderColor: color,
      pointRadius: 0,
      pointHoverRadius: 0,
      hidden: false,
      tension: 0,
      spanGaps: false,
    });
  });

  // Compute x-bounds from all profiles
  let extMin = Number.POSITIVE_INFINITY;
  let extMax = Number.NEGATIVE_INFINITY;
  
  group.profiles.forEach((p) => {
    if (!p.chartPoints?.length) return;
    const distances = p.chartPoints.map(pt => pt.distance);
    const minX = Math.min(...distances);
    const maxX = Math.max(...distances);
    if (minX < extMin) extMin = minX;
    if (maxX > extMax) extMax = maxX;
  });
  
  if (!isFinite(extMin) || !isFinite(extMax)) { extMin = 0; extMax = 1; }

  const xCenter = extMin + (extMax - extMin) / 2;

  return { datasets, baseMin, baseMax, xMin: extMin, xMax: extMax, xCenter };
});

// --- Rendering ---
let rafId: number | null = null;
function scheduleRender() {
  if (rafId != null) return;
  rafId = requestAnimationFrame(() => { rafId = null; renderChart(); });
}

function renderChart() {
  const el = profileCanvas.value;
  if (!el) return;

  // If the canvas element was removed/recreated (v-if toggle), rebuild the chart
  if (chartInstance && chartInstance.canvas !== el) {
    chartInstance.destroy();
    chartInstance = null;
  }

  const ctx = el.getContext("2d");
  if (!ctx) return;

  const c = chartInput.value;
  if (!c.datasets.length) {
    // Clear and draw simple empty state (Chart.js not created in this branch)
    el.width = el.clientWidth;
    el.height = el.clientHeight;
    ctx.clearRect(0, 0, el.width, el.height);
    ctx.fillStyle = "#666";
    ctx.font = "16px Arial";
    ctx.textAlign = "center";
    ctx.fillText("Select a visible profile to view cross-sections", el.width / 2, el.height / 2);
    // If there was an existing chart, destroy it
    if (chartInstance) { chartInstance.destroy(); chartInstance = null; }
    return;
  }

  // Always derive y-bounds from the entire group (use baseMin/baseMax which are calculated from all profiles in world Z)
  // Apply vertical exaggeration to the bounds for drawing, but label ticks show actual elevation
  let visMin = c.baseMin * verticalExaggeration.value;
  let visMax = c.baseMax * verticalExaggeration.value;
  if (!isFinite(visMin) || !isFinite(visMax)) { visMin = 0; visMax = 0; }
  const range = Math.max(1e-6, visMax - visMin);
  const pad = range * 0.1;
  const yMin = visMin - pad, yMax = visMax + pad;

  // Container height is now controlled by the parent Splitpanes component

  const isTransverse = isTransverseGroup(selectedProfileGroup.value);
  const xCenter = getChartCenterX();

  const xTicks: any = { color: "#ccc" };
  if (isTransverse) {
    xTicks.callback = (val: any) => formatTransverseAxisValue(val, xCenter);
  }

  const xScaleOptions: any = {
    type: "linear" as const,
    min: c.xMin,
    max: c.xMax,
    position: "bottom" as const,
    grid: { color: "#444" },
    ticks: xTicks
  };
  if (isTransverse) {
    xScaleOptions.afterBuildTicks = (scale: any) => {
      buildLinearTicks(scale, 10);
      ensureTransverseCenterTick(scale, xCenter);
    };
  } else {
    xScaleOptions.afterBuildTicks = (scale: any) => buildLinearTicks(scale, 10);
  }

  const config = {
    type: "line" as const,
    data: { datasets: c.datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      parsing: false,
      normalized: true,
      layout: {
        padding: {
          top: 20,
          bottom: 50,
          left: 20,
          right: 20
        }
      },
      interaction: { intersect: false, mode: "nearest" as const, axis: "x" as const },
      elements: { point: { radius: 0, hoverRadius: 0 } },
      scales: {
        x: xScaleOptions,
        y: { 
          type: "linear" as const, 
          min: yMin, 
          max: yMax, 
          position: "right" as const,
          grid: { color: "#444" }, 
          ticks: { 
            color: "#ccc",
            // Show actual elevation by undoing exaggeration in labels
            callback: (val: any) => {
              const num = typeof val === "number" ? val : parseFloat(val);
              const ex = verticalExaggeration.value || 1;
              if (!isFinite(num) || !isFinite(ex)) return "";
              return (num / ex).toFixed(2);
            }
          }
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
        decimation: { enabled: false },
      },
      // Add hover callbacks
      onHover: (event: any, elements: any) => {
        handleChartElementHover(event, elements);
      }
    }
  };

  if (!chartInstance) {
    // Custom plugin: vertical guide + moving markers
    const cursorPlugin = {
      id: "profileCursor",
      afterDatasetsDraw(chart: any) {
        if (hoveredChartDistance.value == null) return;
        const xScale = chart.scales?.x, yScale = chart.scales?.y, area = chart.chartArea;
        if (!xScale || !yScale || !area) return;
        const x = hoveredChartDistance.value;
        const xPix = xScale.getPixelForValue(x);
        const ctx = chart.ctx;
        ctx.save();
        // vertical guide
        ctx.strokeStyle = "#666";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(xPix + 0.5, area.top);
        ctx.lineTo(xPix + 0.5, area.bottom);
        ctx.stroke();
        // markers for each visible dataset
        const datasets = chart.data.datasets || [];
        for (let di = 0; di < datasets.length; di++) {
          const ds = datasets[di];
          const meta = chart.getDatasetMeta(di);
          if (!ds || ds.hidden || meta?.hidden) continue;
          const y = interpolateDatasetY(ds.data as any[], x);
          if (y == null) continue;
          const yPix = yScale.getPixelForValue(y);

          // Draw white outline first (larger circle)
          ctx.fillStyle = "#ffffff";
          ctx.beginPath();
          ctx.arc(xPix, yPix, 4.5, 0, Math.PI * 2);
          ctx.fill();

          // Draw colored fill on top (smaller circle)
          ctx.fillStyle = ds.borderColor || "#fff";
          ctx.beginPath();
          ctx.arc(xPix, yPix, 3, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }
    } as any;

    chartInstance = new Chart(el, { ...(config as any), plugins: [cursorPlugin] } as any);
  } else {
    chartInstance.data.datasets = c.datasets as any;
    const opt: any = chartInstance.options;
    opt.scales.x.min = c.xMin; opt.scales.x.max = c.xMax;
    opt.scales.y.min = yMin;   opt.scales.y.max = yMax;
    opt.scales.x.ticks.color = "#ccc";
    if (isTransverse) {
      opt.scales.x.ticks.callback = (val: any) => formatTransverseAxisValue(val, xCenter);
      opt.scales.x.afterBuildTicks = (scale: any) => {
        buildLinearTicks(scale, 10);
        ensureTransverseCenterTick(scale, xCenter);
      };
    } else {
      delete opt.scales.x.ticks.callback;
      opt.scales.x.afterBuildTicks = (scale: any) => buildLinearTicks(scale, 10);
    }
    opt.scales.y.title.text = `Elevation (${getProfileUnit()})${verticalExaggeration.value !== 1 ? ` - visual ${verticalExaggeration.value}x` : ""}`;
    chartInstance.update("none");
  }
}

// Handle hover over chart: drive by mouse x to keep smooth
function handleChartElementHover(event: any, _elements: any) {
  if (!selectedProfileGroup.value || !chartInstance) {
    clearAllHoverMarkers();
    return;
  }

  const xScale = (chartInstance as any).scales?.x;
  const area = (chartInstance as any).chartArea;
  if (!xScale || !area || typeof event?.x !== "number" || typeof event?.y !== "number") {
    return;
  }

  // Outside plot area: hide markers
  if (event.x < area.left || event.x > area.right || event.y < area.top || event.y > area.bottom) {
    clearAllHoverMarkers();
    return;
  }

  // Smooth distance from mouse x
  const hoveredDistance = xScale.getValueForPixel(event.x);
  hoveredChartDistance.value = hoveredDistance;

  const visibleIds = buildVisibleIdSet();
  const selectedProfiles = selectedProfileGroup.value.selectedProfiles || selectedProfileGroup.value.profiles;

  // Show markers on all visible profiles at same distance
  selectedProfiles.forEach(profile => {
    if (!visibleIds.has(profile.uuid)) {
      viewer.setProfileHoverMarker(profile.uuid, null);
      return;
    }

    const position = calculatePositionOnProfile(profile, hoveredDistance, null);
    viewer.setProfileHoverMarker(profile.uuid, position);
  });

  // Update custom tooltip content/position
  updateCustomTooltip(hoveredDistance, event);

  // Force a lightweight redraw so the guide/markers follow smoothly
  const ci: any = chartInstance as any;
  if (ci?.draw) ci.draw();
}

const DISTANCE_EPSILON = 1e-6;

function getChartDistanceInfo(profile: Profile): ChartDistanceCache | null {
  if (!profile?.chartPoints?.length) return null;

  const cached = chartDistanceCache.get(profile);
  if (cached) return cached;

  const distances: number[] = [];
  for (const pt of profile.chartPoints) {
    const d = Number(pt.distance);
    if (!Number.isFinite(d)) {
      return null;
    }
    distances.push(d);
  }

  const first = distances[0];
  const last = distances[distances.length - 1];
  const ascending = last >= first;
  let minVal = first;
  let maxVal = first;
  for (let i = 1; i < distances.length; i++) {
    const val = distances[i];
    if (val < minVal) minVal = val;
    if (val > maxVal) maxVal = val;
  }

  const info: ChartDistanceCache = {
    distances,
    ascending,
    min: minVal,
    max: maxVal
  };
  chartDistanceCache.set(profile, info);
  return info;
}

function getWorldDistanceInfo(profile: Profile): WorldDistanceCache | null {
  const cached = worldDistanceCache.get(profile);
  if (cached && cached.generation === worldDistanceCacheGeneration) {
    return cached;
  }

  const points = typeof (profile as any).getWorldPoints === "function" ? (profile as any).getWorldPoints() : [];
  if (!Array.isArray(points) || !points.length) {
    return null;
  }
  const info: WorldDistanceCache = { points, generation: worldDistanceCacheGeneration };
  worldDistanceCache.set(profile, info);
  return info;
}

function interpolateBetweenPoints(start: any, end: any, t: number) {
  const sx = start?.x ?? 0;
  const sy = start?.y ?? 0;
  const sz = start?.z ?? 0;
  const ex = end?.x ?? sx;
  const ey = end?.y ?? sy;
  const ez = end?.z ?? sz;
  return {
    x: sx + (ex - sx) * t,
    y: sy + (ey - sy) * t,
    z: sz + (ez - sz) * t,
  };
}

function getWorldPositionAtDistance(profile: Profile, distance: number): { x: number; y: number; z: number } | null {
  const chartInfo = getChartDistanceInfo(profile);
  if (!chartInfo) return null;
  const worldInfo = getWorldDistanceInfo(profile);
  if (!worldInfo) return null;

  const distances = chartInfo.distances;
  const points = worldInfo.points;
  const count = Math.min(distances.length, points.length);
  if (!count) return null;

  if (count === 1) {
    const p = points[0];
    return { x: p?.x ?? 0, y: p?.y ?? 0, z: p?.z ?? 0 };
  }

  const first = distances[0];
  const last = distances[count - 1];

  if (chartInfo.ascending) {
    if (distance < first - DISTANCE_EPSILON || distance > last + DISTANCE_EPSILON) {
      return null;
    }
    if (distance <= first + DISTANCE_EPSILON) {
      const p = points[0];
      return { x: p?.x ?? 0, y: p?.y ?? 0, z: p?.z ?? 0 };
    }
    if (distance >= last - DISTANCE_EPSILON) {
      const p = points[count - 1];
      return { x: p?.x ?? 0, y: p?.y ?? 0, z: p?.z ?? 0 };
    }
    for (let i = 1; i < count; i++) {
      const prev = distances[i - 1];
      const curr = distances[i];
      if (distance <= curr + DISTANCE_EPSILON) {
        const span = curr - prev;
        const t = Math.abs(span) <= DISTANCE_EPSILON ? 0 : (distance - prev) / span;
        return interpolateBetweenPoints(points[i - 1], points[i], Math.min(Math.max(t, 0), 1));
      }
    }
  } else {
    if (distance > first + DISTANCE_EPSILON || distance < last - DISTANCE_EPSILON) {
      return null;
    }
    if (distance >= first - DISTANCE_EPSILON) {
      const p = points[0];
      return { x: p?.x ?? 0, y: p?.y ?? 0, z: p?.z ?? 0 };
    }
    if (distance <= last + DISTANCE_EPSILON) {
      const p = points[count - 1];
      return { x: p?.x ?? 0, y: p?.y ?? 0, z: p?.z ?? 0 };
    }
    for (let i = 1; i < count; i++) {
      const prev = distances[i - 1];
      const curr = distances[i];
      if (distance >= curr - DISTANCE_EPSILON) {
        const span = curr - prev;
        const t = Math.abs(span) <= DISTANCE_EPSILON ? 0 : (distance - prev) / span;
        return interpolateBetweenPoints(points[i - 1], points[i], Math.min(Math.max(t, 0), 1));
      }
    }
  }

  return null;
}

// Update the calculatePositionOnProfile function to handle null elevation
function calculatePositionOnProfile(profile: Profile, distance: number, elevation: number | null): {x: number, y: number, z: number} | null {
  if (!profile) return null;
  const worldPos = getWorldPositionAtDistance(profile, distance);
  if (!worldPos) return null;
  if (elevation != null && Number.isFinite(elevation)) {
    worldPos.z = elevation;
  }
  return worldPos;
}

function clearAllHoverMarkers() {
  if (!selectedProfileGroup.value) return;

  const selectedProfiles = selectedProfileGroup.value.selectedProfiles || selectedProfileGroup.value.profiles;
  selectedProfiles.forEach(profile => {
    viewer.setProfileHoverMarker(profile.uuid, null);
  });

  hoveredChartDistance.value = null;
  if (tooltipEl.value) tooltipEl.value.style.display = "none";
}

// Handle 3D hover events from the viewer
function handle3DProfileHover(data: { profileId: string; distance: number } | null) {
  if (!data) {
    // Clear all hover markers and state when hover ends
    if (hoveredChartDistance.value !== null && selectedProfileGroup.value) {
      const selectedProfiles = selectedProfileGroup.value.selectedProfiles || selectedProfileGroup.value.profiles;
      selectedProfiles.forEach(profile => {
        viewer.setProfileHoverMarker(profile.uuid, null);
      });
      hoveredChartDistance.value = null;
      if (tooltipEl.value) tooltipEl.value.style.display = "none";
      if (chartInstance) {
        const ci: any = chartInstance as any;
        if (ci?.draw) ci.draw();
      }
    }
    return;
  }

  if (!selectedProfileGroup.value || !chartInstance) {
    return;
  }

  const { profileId, distance } = data;

  // Check if the hovered profile is part of the currently selected group
  const selectedProfiles = selectedProfileGroup.value.selectedProfiles || selectedProfileGroup.value.profiles;
  const isInGroup = selectedProfiles.some(p => p.uuid === profileId);

  if (!isInGroup) {
    return; // Not in the current group, ignore
  }

  // Update the hovered distance
  hoveredChartDistance.value = distance;

  // Show markers on OTHER visible profiles at the same distance
  // (the hovered profile already has its marker set by the 3D viewer)
  const visibleIds = buildVisibleIdSet();
  selectedProfiles.forEach(profile => {
    if (profile.uuid === profileId) return; // Skip the hovered profile itself

    if (!visibleIds.has(profile.uuid)) {
      viewer.setProfileHoverMarker(profile.uuid, null);
      return;
    }

    const position = calculatePositionOnProfile(profile, distance, null);
    viewer.setProfileHoverMarker(profile.uuid, position);
  });

  // Update tooltip (position it on the chart)
  const xScale = (chartInstance as any).scales?.x;
  const area = (chartInstance as any).chartArea;
  if (xScale && area && profileCanvas.value) {
    const pixelX = xScale.getPixelForValue(distance);
    // Create a synthetic event for the tooltip
    const canvasRect = profileCanvas.value.getBoundingClientRect();
    const syntheticEvent = {
      x: pixelX,
      y: (area.top + area.bottom) / 2, // Middle of chart vertically
      native: { clientX: canvasRect.left + pixelX, clientY: canvasRect.top + (area.top + area.bottom) / 2 }
    };
    updateCustomTooltip(distance, syntheticEvent as any);
  }

  // Force chart redraw
  const ci: any = chartInstance as any;
  if (ci?.draw) ci.draw();
}

// Interpolate y and originalY at distance x for a given dataset's data array
function interpolateDatasetPoint(arr: any[], x: number): { y: number; originalY: number } | null {
  if (!arr || !arr.length) return null;
  // assume arr sorted by x
  const n = arr.length;
  if (x < arr[0].x || x > arr[n - 1].x) return null;
  let lo = 0, hi = n - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const vx = arr[mid].x;
    if (vx < x) lo = mid + 1; else hi = mid - 1;
  }
  const i1 = Math.max(0, Math.min(n - 1, lo));
  const i0 = Math.max(0, i1 - 1);
  const p0 = arr[i0], p1 = arr[i1];
  const dx = Math.max(1e-9, (p1.x - p0.x));
  const t = Math.max(0, Math.min(1, (x - p0.x) / dx));
  const y = p0.y + (p1.y - p0.y) * t;
  const oy0 = p0.originalY ?? p0.y, oy1 = p1.originalY ?? p1.y;
  const originalY = oy0 + (oy1 - oy0) * t;
  return { y, originalY };
}

function interpolateDatasetY(arr: any[], x: number): number | null {
  const p = interpolateDatasetPoint(arr, x);
  return p ? p.y : null;
}

function getProfileDifferenceAtPoint(entries: Array<{ actual: number | null }>): { value: number; label: string } | null {
  const values = entries
    .map(entry => entry.actual)
    .filter((value): value is number => value != null && Number.isFinite(value));

  if (values.length < 2) {
    return null;
  }

  if (values.length === 2) {
    return { value: Math.abs(values[0] - values[1]), label: "Difference" };
  }

  return { value: Math.max(...values) - Math.min(...values), label: "Max difference" };
}

function getChartCenterX(): number {
  const c: any = chartInput.value;
  if (c && typeof c.xCenter === "number" && isFinite(c.xCenter)) {
    return c.xCenter;
  }
  const min = typeof c?.xMin === "number" ? c.xMin : 0;
  const max = typeof c?.xMax === "number" ? c.xMax : min;
  return min + (max - min) / 2;
}

function formatTransverseAxisValue(raw: any, center: number): string {
  const num = typeof raw === "number" ? raw : parseFloat(raw);
  if (!isFinite(num)) return "";
  const shifted = num - center;
  const aligned = Math.abs(shifted) < 1e-9 ? 0 : shifted;
  const absVal = Math.abs(aligned);
  const decimals = absVal >= 100 ? 0 : absVal >= 10 ? 1 : 2;
  return Number(aligned.toFixed(decimals)).toString();
}

function ensureTransverseCenterTick(scale: any, center: number) {
  if (!scale || !Array.isArray(scale.ticks)) return;

  const epsilon = 1e-6;
  const numericTicks: number[] = [];
  scale.ticks.forEach((tick: any) => {
    const value = typeof tick?.value === "number" ? tick.value : parseFloat(tick?.value);
    if (Number.isFinite(value)) numericTicks.push(value);
  });

  if (!numericTicks.length) numericTicks.push(center);
  else if (!numericTicks.some(value => Math.abs(value - center) < epsilon)) numericTicks.push(center);

  numericTicks.sort((a, b) => a - b);
  const centerIndex = numericTicks.findIndex(value => Math.abs(value - center) < epsilon);
  if (centerIndex === -1) {
    scale.ticks = numericTicks.map(value => ({ value }));
    return;
  }

  const scaleCtx: CanvasRenderingContext2D | null = (scale.chart?.ctx || scale.ctx) ?? null;
  const resolveFont = typeof scale._resolveTickFontOptions === "function" ? scale._resolveTickFontOptions.bind(scale) : null;
  const fontString = resolveFont ? resolveFont(0)?.string : undefined;

  const measure = (text: string): number => {
    if (!scaleCtx || typeof scaleCtx.measureText !== "function") return 0;
    const prevFont = scaleCtx.font;
    if (fontString) scaleCtx.font = fontString;
    const width = scaleCtx.measureText(text).width;
    if (fontString) scaleCtx.font = prevFont;
    return width;
  };

  const centerLabel = formatTransverseAxisValue(center, center);
  const centerWidth = measure(centerLabel);
  const centerPixel = typeof scale.getPixelForValue === "function" ? scale.getPixelForValue(center) : 0;
  const paddingPx = 6;

  const fallbackValueGap = Math.abs((scale.max ?? center) - (scale.min ?? center)) * 0.02;

  const shouldCull = (value: number): boolean => {
    const pixelGap = typeof scale.getPixelForValue === "function" ? Math.abs(scale.getPixelForValue(value) - centerPixel) : Math.abs(value - center);
    if (scaleCtx && typeof scaleCtx.measureText === "function") {
      const neighbourLabel = formatTransverseAxisValue(value, center);
      const neighbourWidth = measure(neighbourLabel);
      const minGap = (centerWidth + neighbourWidth) / 2 + paddingPx;
      return pixelGap < minGap;
    }
    return Math.abs(value - center) < Math.max(fallbackValueGap, epsilon);
  };

  const indicesToRemove = new Set<number>();

  for (let i = centerIndex - 1; i >= 0; i--) {
    if (!shouldCull(numericTicks[i])) break;
    indicesToRemove.add(i);
  }
  for (let i = centerIndex + 1; i < numericTicks.length; i++) {
    if (!shouldCull(numericTicks[i])) break;
    indicesToRemove.add(i);
  }

  const remaining = numericTicks.filter((_, idx) => !indicesToRemove.has(idx));
  scale.ticks = remaining.map(value => ({ value }));
}

function buildLinearTicks(scale: any, interval: number) {
  if (!scale) return;
  const min = typeof scale.min === "number" ? scale.min : parseFloat(scale.min);
  const max = typeof scale.max === "number" ? scale.max : parseFloat(scale.max);
  if (!isFinite(min) || !isFinite(max) || interval <= 0) return;

  const ticks: any[] = [];
  const start = Math.ceil(min / interval) * interval;
  for (let v = start; v < max; v += interval) {
    if (v > max - 1e-6) break;
    ticks.push({ value: v });
  }
  // Always include min and max as actual values (no rounding)
  if (!ticks.length || Math.abs(ticks[0].value - min) > 1e-6) ticks.unshift({ value: min });
  if (Math.abs(ticks[ticks.length - 1]?.value - max) > 1e-6) ticks.push({ value: max });

  const filtered: any[] = [];
  for (const tick of ticks) {
    const value = typeof tick?.value === "number" ? tick.value : parseFloat(tick?.value);
    if (!isFinite(value)) continue;
    const last = filtered.length ? filtered[filtered.length - 1].value : null;
    if (last != null && Math.abs(value - last) < interval * 0.25) {
      filtered.pop();
    }
    filtered.push({ value });
  }

  scale.ticks = filtered;
}

function updateCustomTooltip(distanceX: number, _event: any) {
  if (!chartInstance || !tooltipEl.value) return;
  const chart: any = chartInstance;
  const xScale = chart.scales?.x, yScale = chart.scales?.y, area = chart.chartArea;
  if (!xScale || !yScale || !area) return;

  const datasets = (chart.data.datasets as any[]) || [];
  const entries: { label: string; color: string; actual: number | null }[] = [];
  for (let di = 0; di < datasets.length; di++) {
    const ds = datasets[di];
    const meta = chart.getDatasetMeta(di);
    if (!ds || ds.hidden || meta?.hidden) continue;
    const p = interpolateDatasetPoint(ds.data as any[], distanceX);
    entries.push({ label: ds.label, color: ds.borderColor || "#fff", actual: p ? p.originalY : null });
  }
  if (!entries.length) { tooltipEl.value.style.display = "none"; return; }

  const el = tooltipEl.value;
  const group = selectedProfileGroup.value;
  const unit = getProfileUnit(group);
  const profileDifference = getProfileDifferenceAtPoint(entries);
  const titleText = profileDifference == null
    ? null
    : `${profileDifference.label}: ${profileDifference.value.toFixed(2)} ${unit}`;
  const html = [
    ...(titleText ? [`<div class="tt-title">${titleText}</div>`] : []),
    ...entries.map(e => `
      <div class="tt-row">
        <span class="color-dot" style="background:${e.color}; border:1px solid rgba(255,255,255,0.6);"></span>
        <span class="lb">${e.label}:</span>
        <span class="vl">${e.actual == null ? "—" : `${e.actual.toFixed(2)} ${unit}`}</span>
      </div>`)
  ].join("");
  el.innerHTML = html;
  el.style.display = "block";

  // Position near the vertical line within chart area
  const xPix = xScale.getPixelForValue(distanceX);
  const rect = (profileViewerEl.value as HTMLDivElement).getBoundingClientRect();
  const canvasRect = (chart.canvas as HTMLCanvasElement).getBoundingClientRect();
  const left = canvasRect.left - rect.left + (xPix + 8);
  let top = canvasRect.top - rect.top + area.top + 8;
  // prevent overflow
  const elWidth = el.offsetWidth || 160;
  const elHeight = el.offsetHeight || 100;
  const maxLeft = (canvasRect.right - rect.left) - elWidth - 8;
  const maxTop = (canvasRect.bottom - rect.top) - elHeight - 8;
  el.style.left = Math.min(left, maxLeft) + "px";
  el.style.top = Math.min(top, maxTop) + "px";
}

// --- Reactive wiring ---
watch(() => appState.viewType, () => {
  worldDistanceCacheGeneration++;
  hoveredChartDistance.value = null;
  viewer?.clearProfileHoverMarkers?.();
  clearAllHoverMarkers();
  if (tooltipEl.value) {
    tooltipEl.value.style.display = "none";
  }
});

watchEffect(() => {
  const sel = appState.selectedElement;
  let newGroup = null;
  
  // Check if sel is a Profile instance by checking for Profile-specific properties
  const isProfile = sel && typeof sel === "object" && "chartPoints" in sel && Array.isArray(sel.chartPoints);

  if (isProfile) {
    // Individual profile selected - use entire group for scaling but highlight only the selected one
    newGroup = findProfileGroup(sel);
    if (newGroup) {
      // Mark which profiles are actually selected for display purposes
      newGroup.selectedProfiles = [sel];
    } else {
      // Fallback to single profile if no group found
      newGroup = {
        name: sel.name,
        profiles: [sel],
        selectedProfiles: [sel]
      };
    }
  } else if (sel && (sel as any).isMultiProfileSelection) {
    // Multiple profiles selected with Ctrl/Cmd - use entire group for scaling
    const firstProfile = (sel as any).profiles[0];
    if (firstProfile) {
      newGroup = findProfileGroup(firstProfile);
      if (newGroup) {
        // Mark which profiles are actually selected for display purposes
        newGroup.selectedProfiles = (sel as any).profiles;
        newGroup.name = `${(sel as any).profiles.length} Selected Profiles`;
      } else {
        // Fallback to selected profiles only if no group found
        newGroup = {
          name: `${(sel as any).profiles.length} Selected Profiles`,
          profiles: (sel as any).profiles,
          selectedProfiles: (sel as any).profiles
        };
      }
    }
  } else if (sel && (sel as any).isProfileGroup) {
    // Profile group selected from layers menu
    const globalViewer = (window as any).viewer;
    if (globalViewer && (sel as any).layerItem && (sel as any).layerItem.children) {
      const groupProfiles: any[] = [];
      (sel as any).layerItem.children.forEach((child: any) => {
        const profile = globalViewer.profiles.find((p: any) => 
          p.uuid === child.id || p.name === child.name
        );
        if (profile) {
          groupProfiles.push(profile);
        }
      });
      
      newGroup = {
        name: (sel as any).groupName,
        profiles: groupProfiles.sort((a: any, b: any) => a.name.localeCompare(b.name)),
        selectedProfiles: groupProfiles // All profiles in group are considered selected
      };
    }
  }
  
  // Clear markers when changing profile groups
  if (selectedProfileGroup.value && selectedProfileGroup.value !== newGroup) {
    clearAllHoverMarkers();
  }
  
  selectedProfileGroup.value = newGroup;
  if (!selectedProfileGroup.value && chartInstance) { chartInstance.destroy(); chartInstance = null; }
  verticalExaggeration.value; // dep
  appState.view1Layers;       // dep
  appState.view2Layers;       // dep (if relevant in your setup)
  chartInput.value;           // dep: data & bounds
  scheduleRender();
  
  // Viewer resize is now handled by the parent ViewPage component
});

// --- DOM events ---
function onVerticalExaggerationChanged(event: CustomEvent) { if (event.detail && typeof event.detail.exaggeration === "number") verticalExaggeration.value = event.detail.exaggeration; }
const onProfileColorChanged = () => scheduleRender();
const onProfileDataLoaded = () => scheduleRender();

onMounted(() => {
  document.addEventListener("profile-vertical-exaggeration-changed", onVerticalExaggerationChanged as any);
  document.addEventListener("profile-color-changed", onProfileColorChanged as any);
  document.addEventListener("profile-data-loaded", onProfileDataLoaded as any);

  // Listen to 3D profile hover events
  viewer.onProfileHover.addListener(handle3DProfileHover);

  scheduleRender();
});

onUnmounted(() => {
  clearAllHoverMarkers();
  if (chartInstance) { chartInstance.destroy(); chartInstance = null; }
  document.removeEventListener("profile-data-loaded", onProfileDataLoaded as any);
  document.removeEventListener("profile-color-changed", onProfileColorChanged as any);
  document.removeEventListener("profile-vertical-exaggeration-changed", onVerticalExaggerationChanged as any);

  // Remove 3D profile hover listener
  viewer.onProfileHover.removeListener(handle3DProfileHover);
});

// --- Expose ---
function downloadImage() {
  if (!profileCanvas.value) return;
  const link = document.createElement("a");
  link.download = `${appState.project?.name || "profile"}-profile.png`;
  link.href = profileCanvas.value.toDataURL("image/png");
  link.click();
}

// Add close function
function closeProfileViewer() {
  clearAllHoverMarkers();
  
  // Clear viewer profile selections (makes thick lines thin again)
  const globalViewer = (window as any).viewer;
  if (globalViewer) {
    globalViewer.clearProfileMultiSelection?.();
  }
  
  selectedProfileGroup.value = null;
  appState.selectedElement = null;
  if (chartInstance) {
    chartInstance.destroy();
    chartInstance = null;
  }
  
  // Viewer resize is now handled by the parent ViewPage component
}

expose({ renderChart, downloadImage, verticalExaggeration, closeProfileViewer });

return (_ctx: any,_cache: any) => {
  return (selectedProfileGroup.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: "profile-viewer",
        ref_key: "profileViewerEl",
        ref: profileViewerEl
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, "Profile viewer: " + _toDisplayString(selectedProfileGroup.value.name), 1),
          _createElementVNode("div", { class: "toolbar-buttons" }, [
            _createElementVNode("button", {
              onClick: downloadImage,
              class: "toolbar-btn",
              title: "Download image"
            }, _hoisted_4),
            _createElementVNode("button", {
              onClick: closeProfileViewer,
              class: "toolbar-btn",
              title: "Close"
            }, _hoisted_6)
          ])
        ]),
        _createElementVNode("div", _hoisted_7, [
          _createElementVNode("canvas", {
            ref_key: "profileCanvas",
            ref: profileCanvas,
            class: "profile-canvas",
            onMouseleave: clearAllHoverMarkers
          }, null, 544)
        ]),
        _createElementVNode("div", {
          ref_key: "tooltipEl",
          ref: tooltipEl,
          class: "profile-tooltip",
          style: {"display":"none"}
        }, null, 512)
      ], 512))
    : _createCommentVNode("", true)
}
}

})